import React from 'react';
import { Button, Card, Form, Input, Select, Space, Typography } from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { ACTION_TYPE_OPTIONS, BUTTON_STYLE_OPTIONS, MAX_BUTTONS, PLATFORM_OPTIONS, STORE_OPTIONS, newButton } from './constants';

const { Text } = Typography;

function ActionFields({ action, onChange }) {
    switch (action.type) {
        case 'navigate':
            return (
                <Form.Item label="Page" required help="A path on designwithfriends.com, starting with /.">
                    <Input value={action.href ?? ''} onChange={(e) => onChange({ ...action, href: e.target.value })} placeholder="/challenges" />
                </Form.Item>
            );
        case 'open_url':
            return (
                <Form.Item label="URL" required help="Opens in the browser; https only.">
                    <Input value={action.url ?? ''} onChange={(e) => onChange({ ...action, url: e.target.value })} placeholder="https://…" />
                </Form.Item>
            );
        case 'open_challenge':
            return (
                <Form.Item label="Challenge id" help="Leave empty to open the current challenge.">
                    <Input value={action.challengeId ?? ''} onChange={(e) => onChange({ ...action, challengeId: e.target.value || null })} style={{ width: 300 }} />
                </Form.Item>
            );
        case 'request_review':
            return <Text type="secondary">Opens the store review sheet in the apps; does nothing on the web.</Text>;
        case 'watch_ad':
            return <Text type="secondary">Plays a rewarded ad (the "Time interval" placement's keep-designing gate): Google's ad when one is loaded, otherwise our own Premium video with nothing to collect. On the website the dialog stays up until Google's ad was watched; the app plays its wallet ad, which also grants its coins.</Text>;
        case 'open_signup':
            return <Text type="secondary">Opens the sign-up dialog on the website. The apps hide this button (every app user is signed in), so give it website platforms or pair it with a guest audience.</Text>;
        case 'open_store':
            return (
                <Form.Item label="Store" help="On the website a tap also counts as an ads conversion. For the device's store on its own, use the Store buttons block instead.">
                    <Select value={action.store ?? 'ios'} options={STORE_OPTIONS} onChange={(store) => onChange({ ...action, store })} style={{ width: 200 }} />
                </Form.Item>
            );
        default:
            return null;
    }
}

export function ButtonsEditor({ buttons, onChange }) {
    const setButton = (index, patch) => onChange(buttons.map((b, i) => (i === index ? { ...b, ...patch } : b)));
    const move = (index, delta) => {
        const target = index + delta;
        if (target < 0 || target >= buttons.length) return;
        const next = buttons.slice();
        [next[index], next[target]] = [next[target], next[index]];
        onChange(next);
    };

    return (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {buttons.length === 0 && <Text type="secondary">No buttons yet. A dialog needs at least one.</Text>}
            {buttons.map((button, index) => (
                <Card
                    key={index}
                    size="small"
                    title={<Text>Button {index + 1}</Text>}
                    extra={(
                        <Space size={4}>
                            <Button size="small" type="text" icon={<ArrowUpOutlined />} disabled={index === 0} onClick={() => move(index, -1)} title="Move up" />
                            <Button size="small" type="text" icon={<ArrowDownOutlined />} disabled={index === buttons.length - 1} onClick={() => move(index, 1)} title="Move down" />
                            <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => onChange(buttons.filter((_, i) => i !== index))} title="Remove" />
                        </Space>
                    )}
                >
                    <Space size={16} wrap align="start">
                        <Form.Item label="Label" required>
                            <Input value={button.label} maxLength={40} showCount onChange={(e) => setButton(index, { label: e.target.value })} style={{ width: 220 }} />
                        </Form.Item>
                        <Form.Item label="Style">
                            <Select value={button.style ?? 'primary'} options={BUTTON_STYLE_OPTIONS} onChange={(style) => setButton(index, { style })} style={{ width: 140 }} />
                        </Form.Item>
                        <Form.Item label="Action">
                            <Select
                                value={button.action?.type ?? 'dismiss'}
                                options={ACTION_TYPE_OPTIONS}
                                onChange={(type) => setButton(index, { action: type === 'open_store' ? { type, store: 'ios' } : { type } })}
                                style={{ width: 220 }}
                            />
                        </Form.Item>
                    </Space>
                    <ActionFields action={button.action ?? { type: 'dismiss' }} onChange={(action) => setButton(index, { action })} />
                    <Form.Item label="Show on" help="Leave empty for every platform; use it for one store button per store.">
                        <Select
                            mode="multiple"
                            allowClear
                            placeholder="All platforms"
                            value={button.platforms ?? []}
                            options={PLATFORM_OPTIONS}
                            onChange={(platforms) => setButton(index, { platforms })}
                        />
                    </Form.Item>
                </Card>
            ))}
            <Button icon={<PlusOutlined />} disabled={buttons.length >= MAX_BUTTONS} onClick={() => onChange([...buttons, newButton(buttons.length === 0 ? 'primary' : 'secondary')])}>
                Add button
            </Button>
            {buttons.length >= MAX_BUTTONS && <Text type="secondary">At most {MAX_BUTTONS} buttons.</Text>}
        </Space>
    );
}
